// Secure GPT calls through middleware (API key protected on server)

export const chatWithGPT = async (messages, products, availableFilters = null, userLocations = null) => {
  try {
    // Build dynamic filter info for system message
    let filterInfo = '';
    if (availableFilters) {
      filterInfo = `

Available filter options (extracted from current inventory):
- Card Types: ${availableFilters.cardTypes.join(', ')}
- Durations: ${availableFilters.durations.join(', ')} months
- Facilities: ${availableFilters.facilities.join(', ')}
- Cities: ${availableFilters.cities.join(', ')}
- Price Range: $${availableFilters.priceRange.min} - $${availableFilters.priceRange.max}`;
    }

    // Build user locations context
    let locationsContext = '';
    if (userLocations) {
      if (userLocations.home) {
        locationsContext += `\nUser HOME location: ${userLocations.home.address || userLocations.home.city || 'Unknown'} (lat: ${userLocations.home.lat}, lon: ${userLocations.home.lon})`;
      }
      if (userLocations.work) {
        locationsContext += `\nUser WORK location: ${userLocations.work.address || userLocations.work.city || 'Unknown'} (lat: ${userLocations.work.lat}, lon: ${userLocations.work.lon})`;
      }
      if (userLocations.home && userLocations.work) {
        locationsContext += `\n\nIMPORTANT: User has BOTH home and work locations set. You can recommend products based on:
1. Facilities near their home
2. Facilities near their work
3. Facilities along their commute route (use recommend_products_for_route function)`;
      }
    }

    // Prepare messages with system prompt
    const messagesWithSystem = [
      {
        role: "system",
        content: `You are a helpful shopping assistant for FitPass, a multi-sport gym card store.
You help customers find the right gym membership card based on their needs.

You can also help users explore sports facilities in their area and find facilities along their route to work.

Available products:
${JSON.stringify(products.map(p => ({
  id: p.id,
  title: p.title,
  price: p.price,
  card_type: p.card_type,
  duration_months: p.duration_months,
  facilities: p.facilities?.slice(0, 5),
  includes_swimming_pool: p.includes_swimming_pool,
  includes_spa: p.includes_spa,
  includes_personal_training: p.includes_personal_training,
  max_bookings: p.max_bookings_per_month,
  cities: p.cities?.slice(0, 3)
})), null, 2)}${filterInfo}${locationsContext}

FILTERING INSTRUCTIONS:
You have access to show_products_ui function with these filters:
- card_type: Filter by membership tier
- facility: Filter by specific activity/service (look at "facilities" array in products)
- city: Filter by location (look at "cities" array in products)
- duration: Filter by subscription length
- min_price/max_price: Filter by budget (IMPORTANT: prices are in USD)
- requires_spa: Filter cards that MUST include SPA access (set to true)
- requires_swimming_pool: Filter cards that MUST include swimming pool
- requires_personal_training: Filter cards that MUST include personal training

PRICE FILTERING - IMPORTANT:
When user asks for "tańsze", "budżetowe", "najtańsze" (cheaper/budget cards):
- Use max_price: 100 for budget options
When user asks for "droższe", "premium", "luksusowe" (premium/expensive):
- Use min_price: 150 for premium options
When user mentions specific budget like "do 80 zł" or "under $80":
- Use max_price with that value

BOOLEAN FILTERS - IMPORTANT:
When user asks for products WITH specific features:
- "z SPA", "ze spa", "z dostępem do SPA" → requires_spa: true
- "z basenem", "z pływalnią" → requires_swimming_pool: true
- "z trenerem", "z personal training" → requires_personal_training: true
These filters will ONLY show products where the feature is ACTIVE (true), not products where it's false!

HOW TO INFER USER INTENT:
1. Analyze what the user wants (activities, location, budget, duration)
2. Look at the available filter options provided above
3. Match user's natural language to the appropriate filter values
4. Use the facility names EXACTLY as they appear in the product data

Example reasoning process:
- User says "lubię pływać" → Check facilities list → Use facility: "Swimming"
- User says "mieszkam w Warszawie" → Check cities list → Use city: "Warsaw"
- User says "student" → Use card_type: "Student"
- User says "tańsze karnety" → Use max_price: 100
- User says "z spa" → Use requires_spa: true
- User says "premium z basenem" → Use min_price: 150 AND requires_swimming_pool: true

INTENT HANDLING:
When you receive a user intent message (marked with [USER_INTENT: ...]), extract the intent and parameters, then use your judgment to provide the best response:

- Intent "purchase" with product_id:
  * Extract product_id from the message (format: [USER_INTENT: purchase | product_id: {id}])
  * Call initiate_checkout with the product_id
  * Don't ask questions - user already made their decision by clicking "Kup"

CHECKOUT FLOW - IMPORTANT:
When user says "kup teraz", "kupuję", "zamawiam" AFTER you already called initiate_checkout:
- Check conversation history - if you see "Przygotowuję proces płatności" in recent messages, the checkout is ALREADY in progress
- DO NOT call initiate_checkout again - this creates a loop!
- Instead, respond with a message like: "Proces płatności jest już uruchomiony. Proszę chwilę poczekać na przekierowanie do płatności Stripe."
- If there's no recent checkout message, then call initiate_checkout

- Intent "explore" with product_id:
  * Extract product_id from the message (format: [USER_INTENT: explore | product_id: {id}])
  * Call show_product_details with the product_id to display full product information
  * This will show comprehensive specs, facilities, and cities

- Intent "find_similar" with cardType:
  * Use show_products_ui with card_type filter to show similar products
  * Add helpful commentary about the card type

- Intent "find_products_for_facility" (format: [USER_INTENT: find_products_for_facility | facility_name: X | facility_type: Y | city: Z]):
  * User clicked "Kup karnet" (Buy Pass) on a specific facility
  * Extract facility_name, facility_type, city from the intent message
  * MATCH facility type to product facilities intelligently:
    - "Fitness Center" → look for products with "Gym", "CrossFit", "Martial Arts", "Climbing"
    - "Yoga Studio" → look for products with "Yoga", "Pilates"
    - "Swimming Pool" → look for products with "Swimming" AND requires_swimming_pool: true
    - "Sports Center" → look for products with "Gym" or general multi-sport
  * Filter by city if provided and not "nieznane miasto"
  * Call show_products_ui with appropriate facility and city filters
  * Add personalized Polish message like: "Świetny wybór! Oto karnety FitPass które dają dostęp do {facilityName} i podobnych obiektów..."
  * DO NOT ask questions - user wants to see products immediately!

IMPORTANT - WHEN USER ASKS ABOUT FACILITIES/GYMS FOR A SPECIFIC PRODUCT:
When user asks something like "pokaż siłownie dla produktu X" or "gdzie mogę korzystać z karnetu Y" or "jakie siłownie są dostępne z tym karnetem":
1. First find the product by name in the available products list
2. Look at the product's "facilities" array (e.g., ["Gym", "Swimming", "Yoga"])
3. Call show_facilities_ui with facility_type matching one of the product's facilities
4. If user also mentions a city, add the city filter too
5. ALWAYS use show_facilities_ui for these queries - NOT show_products_ui!

Example:
- User: "pokaż siłownie w których mogę korzystać z FitPass Weekend Warrior"
- You find "Weekend Warrior" has facilities: ["Gym", "CrossFit"]
- Call show_facilities_ui with facility_type: "Fitness Center" (maps to Gym)
- DON'T show products - user already knows the product, they want to see FACILITIES!

LOCATION-BASED RECOMMENDATIONS:
When user has home and/or work locations set, you can provide personalized recommendations:

1. "Jaki karnet będzie dla mnie najlepszy?" (What pass is best for me?)
   - If user has BOTH home and work: use recommend_products_for_route to find passes covering facilities along their commute
   - If user has only home: recommend passes with good coverage in their home city
   - If user has only work: recommend passes with gyms near their workplace

2. "Pokaż siłownie blisko mojego domu" (Show gyms near my home)
   - Use show_facilities_ui with lat/lon from user's home location

3. "Pokaż siłownie po drodze do pracy" (Show gyms along my commute)
   - Use recommend_products_for_route to find facilities and matching products

4. When making location-based recommendations, be specific:
   - "Znalazłem 5 siłowni po drodze z [home] do [work]"
   - "Dla Twojej lokalizacji polecam karnet X, który daje dostęp do [liczba] obiektów w promieniu 5km od domu/pracy"

Be intelligent and contextual. Don't blindly execute actions - think about what's best for the user!
Be friendly, concise, and helpful. Always respond in Polish when user writes in Polish.`
      },
      ...messages
    ];

    // Define functions
    const functions = [
      {
        name: "show_facilities_ui",
        description: "Display sports facilities in an interactive UI with optional filters. Use this when user asks about gyms, sports centers, pools, fitness centers, or facilities in a specific area. Can filter by city, facility type, and location. IMPORTANT: Always use POLISH city names (Warszawa not Warsaw, Kraków not Cracow).",
        parameters: {
          type: "object",
          properties: {
            city: {
              type: "string",
              description: "Filter by city using POLISH name (e.g., 'Warszawa' NOT 'Warsaw', 'Kraków' NOT 'Cracow')"
            },
            facility_type: {
              type: "string",
              description: "Filter by facility type (e.g., 'Fitness Center', 'Swimming Pool', 'Sports Hall')"
            },
            lat: {
              type: "number",
              description: "Latitude for location-based search"
            },
            lon: {
              type: "number",
              description: "Longitude for location-based search"
            },
            max_distance: {
              type: "number",
              description: "Maximum distance in meters from location (default: 5000)"
            }
          }
        }
      },
      {
        name: "show_products_ui",
        description: "Display products in an interactive MCP UI showcase with optional filters. Use this when user wants to see, browse, or explore products. Can filter by card type, price, duration, specific facilities (like yoga, swimming, gym), cities, and premium features.",
        parameters: {
          type: "object",
          properties: {
            card_type: {
              type: "string",
              description: "Filter by card type (available types are provided in system message)"
            },
            min_price: {
              type: "number",
              description: "Minimum price in USD"
            },
            max_price: {
              type: "number",
              description: "Maximum price in USD"
            },
            duration: {
              type: "number",
              description: "Filter by duration in months (available durations are provided in system message)"
            },
            facility: {
              type: "string",
              description: "Filter by specific facility/activity (available facilities are provided in system message)"
            },
            city: {
              type: "string",
              description: "Filter by city (available cities are provided in system message)"
            },
            requires_personal_training: {
              type: "boolean",
              description: "Filter cards that include personal training sessions"
            },
            requires_swimming_pool: {
              type: "boolean",
              description: "Filter cards that include swimming pool access"
            },
            requires_spa: {
              type: "boolean",
              description: "Filter cards that include spa and wellness access"
            }
          }
        }
      },
      {
        name: "initiate_checkout",
        description: "Start checkout process for a specific product",
        parameters: {
          type: "object",
          properties: {
            product_id: {
              type: "string",
              description: "The product ID to purchase"
            }
          },
          required: ["product_id"]
        }
      },
      {
        name: "show_product_details",
        description: "Display detailed information about a specific product including full specs, all facilities, cities, and features. Use this when user wants to explore or learn more about a product.",
        parameters: {
          type: "object",
          properties: {
            product_id: {
              type: "string",
              description: "The product ID to show details for"
            }
          },
          required: ["product_id"]
        }
      },
      {
        name: "recommend_products_for_route",
        description: "Find sports facilities along the user's commute route (home to work) and recommend the best FitPass products based on those facilities. Use this when user asks for location-based recommendations, 'best pass for my commute', 'gyms along my route to work', etc. REQUIRES user to have both home AND work locations set.",
        parameters: {
          type: "object",
          properties: {
            max_detour: {
              type: "number",
              description: "Maximum detour in meters from the direct route (default: 1000 = 1km)"
            },
            facility_type: {
              type: "string",
              description: "Optional filter by facility type (e.g., 'Fitness Center', 'Swimming Pool')"
            }
          }
        }
      }
    ];

    // Call middleware instead of OpenAI directly
    const response = await fetch(`${process.env.REACT_APP_MCP_MIDDLEWARE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messagesWithSystem,
        functions: functions
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message;

  } catch (error) {
    console.error('GPT API Error:', error);
    throw error;
  }
};